import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import PerpetualOrderDetailModal from './PerpetualOrderDetailModal';
import {
  PERPETUAL_ENTRUST_STATUS_OPTIONS,
  PERPETUAL_ORDER_STATUS_LABELS,
  PERPETUAL_ORDER_TREND_OPTIONS,
  PERPETUAL_PAGE_SCENE,
  MEMBER_ROLE_OPTIONS,
  buildPerpetualOrderPageParams,
  defaultPerpetualOrderTimeRange,
  formatAmount,
  formatOptionalPrice,
  validatePerpetualOrderTimeRange,
} from './perpetualOrderConstants';

const DEFAULT_RANGE = defaultPerpetualOrderTimeRange();

const EMPTY_FILTERS = {
  status: '',
  roleId: '',
  orderType: '',
  orderSerialId: '',
  memberIdsCsv: '',
  phoneEmail: '',
  inviterId: '',
  orderTimeFromInput: DEFAULT_RANGE.orderTimeFromInput,
  orderTimeToInput: DEFAULT_RANGE.orderTimeToInput,
};

export default function PerpetualEntrustmentsPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailRow, setDetailRow] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.perpetualOrdersPage(
        buildPerpetualOrderPageParams(pageNo, nextFilters, PERPETUAL_PAGE_SCENE.ENTRUSTMENT)
      );
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    const rangeError = validatePerpetualOrderTimeRange(
      filters.orderTimeFromInput,
      filters.orderTimeToInput
    );
    if (rangeError) {
      setError(rangeError);
      return;
    }
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    loadRecords(1, reset);
  }

  async function handleCancelConfirm() {
    if (!cancelTarget) {
      return;
    }
    setCancelling(true);
    setError('');
    try {
      await adminApi.cancelPerpetualEntrustment(cancelTarget.id);
      setCancelTarget(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '撤單失敗');
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="page-card">
      <h2>永續合約委託單</h2>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="entrustStatus">訂單狀態</label>
          <select
            id="entrustStatus"
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            {PERPETUAL_ENTRUST_STATUS_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="entrustOrderType">投注類型</label>
          <select
            id="entrustOrderType"
            value={filters.orderType}
            onChange={(e) => updateFilter('orderType', e.target.value)}
          >
            {PERPETUAL_ORDER_TREND_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="entrustRoleId">帳號類型</label>
          <select
            id="entrustRoleId"
            value={filters.roleId}
            onChange={(e) => updateFilter('roleId', e.target.value)}
          >
            {MEMBER_ROLE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="entrustOrderSerialId">訂單 ID</label>
          <input
            id="entrustOrderSerialId"
            value={filters.orderSerialId}
            onChange={(e) => updateFilter('orderSerialId', e.target.value)}
            placeholder="請輸入訂單 ID"
          />
        </div>

        <div className="form-field">
          <label htmlFor="entrustMemberIds">用戶 ID</label>
          <input
            id="entrustMemberIds"
            value={filters.memberIdsCsv}
            onChange={(e) => updateFilter('memberIdsCsv', e.target.value)}
            placeholder="請輸入用戶 ID"
          />
        </div>

        <div className="form-field">
          <label htmlFor="entrustPhoneEmail">用戶郵箱/手機號</label>
          <input
            id="entrustPhoneEmail"
            value={filters.phoneEmail}
            onChange={(e) => updateFilter('phoneEmail', e.target.value)}
            placeholder="請輸入用戶郵箱/手機號"
          />
        </div>

        <div className="form-field">
          <label htmlFor="entrustInviterId">直屬上級用戶 ID</label>
          <input
            id="entrustInviterId"
            value={filters.inviterId}
            onChange={(e) => updateFilter('inviterId', e.target.value)}
            placeholder="請輸入直屬上級用戶 ID"
          />
        </div>

        <div className="form-field">
          <label htmlFor="entrustTimeFrom">委託時間起</label>
          <input
            id="entrustTimeFrom"
            type="datetime-local"
            value={filters.orderTimeFromInput}
            onChange={(e) => updateFilter('orderTimeFromInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="entrustTimeTo">委託時間迄</label>
          <input
            id="entrustTimeTo"
            type="datetime-local"
            value={filters.orderTimeToInput}
            onChange={(e) => updateFilter('orderTimeToInput', e.target.value)}
          />
        </div>

        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-scroll">
        <table className="data-table data-table--wide">
          <thead>
            <tr>
              <th>訂單 ID</th>
              <th>訂單狀態</th>
              <th>用戶 ID</th>
              <th>委託金額</th>
              <th>委託價</th>
              <th>委託時間</th>
              <th>成交時間</th>
              <th>撤單時間</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={9} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id || row.orderSerialId}>
                  <td className="mono-cell">{row.orderSerialId || '—'}</td>
                  <td>
                    {PERPETUAL_ORDER_STATUS_LABELS[row.status] || row.statusLabel || row.status || '—'}
                  </td>
                  <td>{row.memberId ?? '—'}</td>
                  <td>{formatAmount(row.betAmount)}</td>
                  <td>{formatOptionalPrice(row.orderPrice)}</td>
                  <td>{formatDateTime(row.createTime)}</td>
                  <td>{row.matchTime ? formatDateTime(row.matchTime) : '—'}</td>
                  <td>{row.cancelTime ? formatDateTime(row.cancelTime) : '—'}</td>
                  <td>
                    <div className="table-actions">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setDetailRow(row)}
                      >
                        詳情
                      </button>
                      {row.status === 'PENDING' && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setCancelTarget(row)}
                        >
                          撤單
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={(p) => loadRecords(p, filters)} />

      <PerpetualOrderDetailModal
        open={Boolean(detailRow)}
        row={detailRow}
        onClose={() => setDetailRow(null)}
        title="合約委託單詳情"
      />

      <ConfirmDialog
        open={Boolean(cancelTarget)}
        title="確認撤單"
        detail={cancelTarget ? `訂單 ${cancelTarget.orderSerialId}` : ''}
        message="確定要撤銷此限價委託？"
        confirmLabel="撤單"
        confirming={cancelling}
        onConfirm={handleCancelConfirm}
        onClose={() => !cancelling && setCancelTarget(null)}
      />
    </div>
  );
}
